/**
 * PageRegistryContext — ADR-005 FE consumer.
 *
 * Fetches the user's visible sidebar pages from `GET /api/pages`, groups
 * them by `page_group`, exposes optimistic mutations, and stale-while-
 * revalidates from sessionStorage for zero-flash reloads. A WS frame named
 * `page_registry_invalidated` (wired by the director at integration time)
 * should call `refresh()` on receipt; for now the context exposes a
 * `notifyInvalidated()` method the ChatContext can call once the frame is
 * threaded through.
 *
 * Feature-flag discipline: this provider is cheap to mount always — it
 * fires one fetch on mount and caches locally. Sidebar.tsx (the only
 * consumer in Phase 1) honours `VITE_SIDEBAR_REGISTRY` to decide whether
 * to render from `groups` (flag ON) or the hardcoded arrays (flag OFF).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

export interface SidebarPage {
  id: string
  client_id: string
  owner_user_id: string | null
  label: string
  slug: string
  icon: string | null
  route: string
  page_group: string
  order_index: number
  visibility: 'owner' | 'all' | 'role:admin' | 'role:operator'
  artifact_id: string | null
  created_by: 'system' | 'user' | 'chat'
  source_turn_id: string | null
  version: number
  status: 'active' | 'archived'
  created_at: string
  updated_at: string
}

export interface SidebarGroup {
  key: string
  pages: SidebarPage[]
}

export interface PinInput {
  label: string
  slug: string
  route: string
  icon?: string | null
  page_group?: string
  artifact_id?: string | null
  created_by?: 'user' | 'chat'
  source_turn_id?: string | null
  visibility?: 'owner' | 'all'
}

/**
 * V3 Page 07 — chat-to-sidebar pin input. Chat emits a draft ArtifactSpec
 * (not yet persisted) + suggested label/group; FE calls `pinArtifact()`
 * which POSTs to /api/pages/pin-artifact. Server creates the artifact +
 * sidebar row atomically in one PG transaction.
 */
export interface PinArtifactInput {
  spec: Record<string, unknown>
  label?: string
  group?: string
  icon?: string
  source_turn_id?: string
}

export interface PinArtifactResult {
  already_pinned: boolean
  page: SidebarPage
  artifact: { id: string; name: string; kind: string; slug?: string | null }
  existing_group?: string
}

export interface PageRegistryValue {
  groups: SidebarGroup[]
  pages: SidebarPage[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
  pinPage: (input: PinInput) => Promise<SidebarPage>
  pinArtifact: (input: PinArtifactInput) => Promise<PinArtifactResult>
  unpinPage: (pageId: string) => Promise<void>
  reorderPages: (pageGroup: string, orderedIds: string[]) => Promise<SidebarPage[]>
  renamePage: (pageId: string, label: string) => Promise<SidebarPage>
  movePageToGroup: (pageId: string, pageGroup: string) => Promise<SidebarPage>
  /** Called when a `page_registry_invalidated` WS frame arrives. */
  notifyInvalidated: () => void
}

export const PageRegistryContext = createContext<PageRegistryValue | null>(null)

// ── Hardcoded V2.6.1 fallback seed ────────────────────────────────────────
//
// Used when the API is unreachable (5xx) OR when the registry flag is OFF
// so the Sidebar can render its original list without reaching across the
// network. Mirror of the current `OPERATE` + `CONFIGURE` arrays in
// Sidebar.tsx, tagged with `created_by='system'` so consumers treat them
// the same as server-returned system rows.

export const HARDCODED_SEED: SidebarPage[] = [
  mkSystem('system-home',      'Home',         'home',      'LayoutDashboard',    '/home',              'Operate',     0),
  mkSystem('system-events',    'Events',       'events',    'Calendar',           '/events',            'Operate',     1),
  mkSystem('system-analytics', 'War Room',     'analytics', 'BarChart3',          '/analytics',         'Operate',     2),
  mkSystem('system-import',    'Smart Import', 'import',    'FileUp',             '/import',            'Operate',     3),
  mkSystem('system-reports',   'Canvases',     'canvases',  'LayoutTemplate',     '/reports',           'Operate',     4),
  mkSystem('system-landing',   'Landing',      'landing',   'FileImage',          '/landing',           'Operate',     5),
  mkSystem('system-compare',   'Compare',      'compare',   'SplitSquareVertical','/analytics/compare', 'Comparisons', 0),
  mkSystem('system-settings',  'Settings',     'settings',  'Settings2',          '/settings',          'Configure',   0),
]

function mkSystem(
  id: string, label: string, slug: string, icon: string,
  route: string, page_group: string, order_index: number,
): SidebarPage {
  return {
    id, client_id: 'butler', owner_user_id: null,
    label, slug, icon, route, page_group, order_index,
    visibility: 'all', artifact_id: null,
    created_by: 'system', source_turn_id: null,
    version: 1, status: 'active',
    created_at: '1970-01-01T00:00:00.000Z',
    updated_at: '1970-01-01T00:00:00.000Z',
  }
}

// ── Grouping helper ──────────────────────────────────────────────────────

const GROUP_ORDER = [
  'Operate', 'Reports', 'Analytics', 'Comparisons',
  'Canvases', 'Resources', 'Configure',
]

export function groupPages(pages: SidebarPage[]): SidebarGroup[] {
  const buckets = new Map<string, SidebarPage[]>()
  for (const p of pages) {
    if (!buckets.has(p.page_group)) buckets.set(p.page_group, [])
    buckets.get(p.page_group)!.push(p)
  }
  for (const arr of buckets.values()) {
    arr.sort((a, b) => a.order_index - b.order_index)
  }
  const keys = Array.from(buckets.keys()).sort((a, b) => {
    const ai = GROUP_ORDER.indexOf(a)
    const bi = GROUP_ORDER.indexOf(b)
    if (ai === -1 && bi === -1) return a.localeCompare(b)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
  return keys.map((key) => ({ key, pages: buckets.get(key)! }))
}

// ── Persistence helpers ───────────────────────────────────────────────────
//
// The cache is **namespaced by (client, user) scope** (Codex pass-1 HIGH
// fix). Without scoping, a tab that served user A's pinned pages would
// silently hand those rows to user B if B signed in on the same tab
// before the first successful /api/pages fetch landed. When no scopeKey
// is supplied (pre-auth or test context without Auth), persistence is
// disabled entirely — the provider uses in-memory state only.

const CACHE_KEY_PREFIX = 'butler.page-registry.v1'

function cacheKey(scopeKey: string | null): string | null {
  if (!scopeKey) return null
  return `${CACHE_KEY_PREFIX}::${scopeKey}`
}

function readCache(scopeKey: string | null): SidebarPage[] | null {
  if (typeof window === 'undefined') return null
  const key = cacheKey(scopeKey)
  if (!key) return null
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    return parsed
  } catch {
    return null
  }
}

function writeCache(scopeKey: string | null, pages: SidebarPage[]) {
  if (typeof window === 'undefined') return
  const key = cacheKey(scopeKey)
  if (!key) return
  try {
    window.sessionStorage.setItem(key, JSON.stringify(pages))
  } catch {
    /* storage full / private mode — ignore */
  }
}

/**
 * Drop every page-registry cache entry in sessionStorage. Director calls
 * this from AuthContext.logout so the next signed-in user never sees the
 * previous user's pinned pages.
 */
export function clearAllPageRegistryCaches() {
  if (typeof window === 'undefined') return
  try {
    const keys: string[] = []
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const k = window.sessionStorage.key(i)
      if (k && k.startsWith(CACHE_KEY_PREFIX)) keys.push(k)
    }
    for (const k of keys) window.sessionStorage.removeItem(k)
  } catch {
    /* ignore */
  }
}

// ── Provider ─────────────────────────────────────────────────────────────

interface PageRegistryProviderProps {
  children: React.ReactNode
  /** Injected for tests; defaults to `fetch`. */
  fetcher?: typeof fetch
  /** Disable the fetch entirely; always render hardcoded seed. */
  registryEnabled?: boolean
  /**
   * `scopeKey` namespaces the sessionStorage cache per signed-in user.
   * Director should pass `${clientId}:${userId}` at integration time so
   * a logout+fresh-login on the same tab can't leak rows across users.
   * When null (default), the cache is disabled entirely — the provider
   * runs in-memory only.
   */
  scopeKey?: string | null
}

export function PageRegistryProvider({
  children,
  fetcher,
  registryEnabled = true,
  scopeKey = null,
}: PageRegistryProviderProps) {
  const doFetch = fetcher || ((typeof fetch !== 'undefined' ? fetch : null) as typeof fetch)
  const [pages, setPages] = useState<SidebarPage[]>(() => readCache(scopeKey) || HARDCODED_SEED)
  const [loading, setLoading] = useState<boolean>(registryEnabled)
  const [error, setError] = useState<string | null>(null)
  const sequenceRef = useRef(0)

  // On scopeKey change (user switched accounts within the tab) drop the
  // in-memory state back to the hardcoded seed so nothing from the prior
  // user lingers until the first fetch completes.
  useEffect(() => {
    setPages(readCache(scopeKey) || HARDCODED_SEED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey])

  // Commit helper — write-through to sessionStorage + advance sequence so
  // stale in-flight requests can't stomp fresher state. Cache is only
  // persisted when the provider was handed a scopeKey; tests without a
  // scopeKey run in-memory only.
  const commitPages = useCallback((next: SidebarPage[]) => {
    sequenceRef.current += 1
    setPages(next)
    writeCache(scopeKey, next)
  }, [scopeKey])

  const refresh = useCallback(async () => {
    if (!registryEnabled || !doFetch) {
      setLoading(false)
      return
    }
    const mySeq = ++sequenceRef.current
    setLoading(true)
    try {
      const res = await doFetch('/api/pages', { credentials: 'same-origin' })
      if (!res.ok) {
        // 4xx unauthorized OR 5xx outage — drop any cached rows so a prior
        // user's data can't leak through (Codex pass-1 HIGH fix). 5xx
        // additionally surfaces a sticky error.
        if (res.status >= 500) {
          setError('Sidebar sync offline, using last-known navigation')
        } else {
          setError(null)
          if (res.status === 401 || res.status === 403) {
            clearAllPageRegistryCaches()
          }
        }
        if (mySeq === sequenceRef.current) {
          setPages(readCache(scopeKey) || HARDCODED_SEED)
        }
        return
      }
      const data = await res.json()
      if (mySeq !== sequenceRef.current) return // superseded; discard
      const list: SidebarPage[] = Array.isArray(data?.pages) ? data.pages : []
      setError(null)
      setPages(list)
      writeCache(scopeKey, list)
    } catch (err) {
      if (mySeq === sequenceRef.current) {
        setError('Sidebar sync offline, using last-known navigation')
        setPages(readCache(scopeKey) || HARDCODED_SEED)
      }
    } finally {
      if (mySeq === sequenceRef.current) setLoading(false)
    }
  }, [doFetch, registryEnabled, scopeKey])

  useEffect(() => {
    // Refetch on mount AND whenever `scopeKey` changes (Codex pass-2 HIGH
    // fix). Without the scopeKey dependency, a logout → login cycle on
    // the same tab leaves the provider stuck on the seed/stale cache
    // until a manual refresh(), and the ownership claims drift silently.
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey])

  // ── Optimistic mutation helpers ────────────────────────────────────────

  const pinPage = useCallback(
    async (input: PinInput): Promise<SidebarPage> => {
      if (!doFetch) throw new Error('fetch unavailable')
      // Optimistic insert uses a temp id + order_index = max+1 in that group.
      const tempId = `opt_${Math.random().toString(36).slice(2, 10)}`
      const group = input.page_group || 'Canvases'
      const now = new Date().toISOString()
      const maxOrder = pages
        .filter((p) => p.page_group === group && p.status === 'active')
        .reduce((acc, p) => Math.max(acc, p.order_index), -1)
      const optimistic: SidebarPage = {
        id: tempId,
        client_id: 'butler',
        owner_user_id: null,
        label: input.label,
        slug: input.slug,
        icon: input.icon ?? null,
        route: input.route,
        page_group: group,
        order_index: maxOrder + 1,
        visibility: input.visibility ?? 'owner',
        artifact_id: input.artifact_id ?? null,
        created_by: input.created_by ?? 'user',
        source_turn_id: input.source_turn_id ?? null,
        version: 1,
        status: 'active',
        created_at: now,
        updated_at: now,
      }
      const before = pages
      commitPages([...pages, optimistic])
      try {
        const res = await doFetch('/api/pages', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        })
        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: 'http_error' }))
          commitPages(before)
          throw new Error(body?.error || `pin_failed_${res.status}`)
        }
        const data = await res.json()
        const server = data.page as SidebarPage
        commitPages([...before, server])
        return server
      } catch (err) {
        commitPages(before)
        throw err
      }
    },
    [doFetch, pages, commitPages],
  )

  // V3 Page 07 — pin a chat-built ArtifactSpec draft to the sidebar.
  // Server creates the artifact + pages row atomically. Duplicate pins
  // return 200 with `already_pinned: true` and the existing page row;
  // we still upsert the returned page into local state so the sidebar
  // lights up either way.
  const pinArtifact = useCallback(
    async (input: PinArtifactInput): Promise<PinArtifactResult> => {
      if (!doFetch) throw new Error('fetch unavailable')
      const res = await doFetch('/api/pages/pin-artifact', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      })
      if (res.status === 429) {
        const body = await res.json().catch(() => ({}))
        const err = new Error('rate_limit_exceeded') as Error & {
          retryAfterSeconds?: number
        }
        err.retryAfterSeconds = body?.retry_after_seconds
        throw err
      }
      if (!res.ok && res.status !== 201 && res.status !== 200) {
        const body = await res.json().catch(() => ({ error: 'http_error' }))
        throw new Error(body?.error || `pin_artifact_failed_${res.status}`)
      }
      const data = (await res.json()) as PinArtifactResult
      // Upsert into local state so the pin is visible even if the WS
      // invalidation frame arrives later / gets deduped. Idempotent by id.
      const existingIdx = pages.findIndex((p) => p.id === data.page.id)
      if (existingIdx === -1) {
        commitPages([...pages, data.page])
      } else {
        commitPages(pages.map((p) => (p.id === data.page.id ? data.page : p)))
      }
      return data
    },
    [doFetch, pages, commitPages],
  )

  const unpinPage = useCallback(
    async (pageId: string): Promise<void> => {
      if (!doFetch) throw new Error('fetch unavailable')
      const before = pages
      commitPages(pages.filter((p) => p.id !== pageId))
      try {
        const res = await doFetch(`/api/pages/${encodeURIComponent(pageId)}`, {
          method: 'DELETE',
          credentials: 'same-origin',
        })
        if (!res.ok && res.status !== 204) {
          commitPages(before)
          throw new Error(`unpin_failed_${res.status}`)
        }
      } catch (err) {
        commitPages(before)
        throw err
      }
    },
    [doFetch, pages, commitPages],
  )

  const patchPage = useCallback(
    async (pageId: string, changes: { label?: string; page_group?: string }): Promise<SidebarPage> => {
      if (!doFetch) throw new Error('fetch unavailable')
      const current = pages.find((p) => p.id === pageId)
      if (!current) throw new Error('page_not_in_local_registry')
      const before = pages
      const optimistic: SidebarPage = { ...current, ...changes }
      commitPages(pages.map((p) => (p.id === pageId ? optimistic : p)))
      try {
        const res = await doFetch(`/api/pages/${encodeURIComponent(pageId)}`, {
          method: 'PATCH',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ version: current.version, ...changes }),
        })
        if (res.status === 409) {
          // Server returns `{error:'version_conflict', page:<fresh>}`.
          const body = await res.json().catch(() => null)
          if (body?.page) {
            commitPages(before.map((p) => (p.id === pageId ? (body.page as SidebarPage) : p)))
          } else {
            commitPages(before)
          }
          throw new Error('version_conflict')
        }
        if (!res.ok) {
          commitPages(before)
          throw new Error(`patch_failed_${res.status}`)
        }
        const body = await res.json()
        const server = body.page as SidebarPage
        commitPages(before.map((p) => (p.id === pageId ? server : p)))
        return server
      } catch (err) {
        if ((err as Error).message !== 'version_conflict') {
          commitPages(before)
        }
        throw err
      }
    },
    [doFetch, pages, commitPages],
  )

  const renamePage = useCallback(
    (pageId: string, label: string) => patchPage(pageId, { label }),
    [patchPage],
  )

  const movePageToGroup = useCallback(
    (pageId: string, pageGroup: string) => patchPage(pageId, { page_group: pageGroup }),
    [patchPage],
  )

  const reorderPages = useCallback(
    async (pageGroup: string, orderedIds: string[]): Promise<SidebarPage[]> => {
      if (!doFetch) throw new Error('fetch unavailable')
      const before = pages
      // Optimistic reassignment of order_index within the group.
      const optimistic = pages.map((p) => {
        if (p.page_group !== pageGroup) return p
        const idx = orderedIds.indexOf(p.id)
        if (idx === -1) return p
        return { ...p, order_index: idx }
      })
      commitPages(optimistic)
      try {
        const res = await doFetch('/api/pages/reorder', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ page_group: pageGroup, ordered_ids: orderedIds }),
        })
        if (!res.ok) {
          commitPages(before)
          throw new Error(`reorder_failed_${res.status}`)
        }
        const body = await res.json()
        const serverPages = Array.isArray(body?.pages) ? body.pages : []
        commitPages([
          ...before.filter((p) => p.page_group !== pageGroup),
          ...serverPages,
        ])
        return serverPages
      } catch (err) {
        commitPages(before)
        throw err
      }
    },
    [doFetch, pages, commitPages],
  )

  const notifyInvalidated = useCallback(() => {
    refresh()
  }, [refresh])

  const groups = useMemo(() => groupPages(pages.filter((p) => p.status === 'active')), [pages])

  const value: PageRegistryValue = {
    groups,
    pages,
    loading,
    error,
    refresh,
    pinPage,
    pinArtifact,
    unpinPage,
    reorderPages,
    renamePage,
    movePageToGroup,
    notifyInvalidated,
  }

  return (
    <PageRegistryContext.Provider value={value}>
      {children}
    </PageRegistryContext.Provider>
  )
}

export function useSidebarPages(): PageRegistryValue {
  const ctx = useContext(PageRegistryContext)
  if (!ctx) {
    // Defensive default — a component mounted without the provider still
    // gets a functional value backed by the hardcoded seed, so Sidebar.tsx
    // can't crash the shell even if App.tsx forgets to wrap. Mutations
    // throw a clear error instead of silently pretending to succeed.
    return {
      groups: groupPages(HARDCODED_SEED),
      pages: HARDCODED_SEED,
      loading: false,
      error: null,
      refresh: async () => {},
      pinPage: async () => { throw new Error('PageRegistryProvider not mounted') },
      pinArtifact: async () => { throw new Error('PageRegistryProvider not mounted') },
      unpinPage: async () => { throw new Error('PageRegistryProvider not mounted') },
      reorderPages: async () => { throw new Error('PageRegistryProvider not mounted') },
      renamePage: async () => { throw new Error('PageRegistryProvider not mounted') },
      movePageToGroup: async () => { throw new Error('PageRegistryProvider not mounted') },
      notifyInvalidated: () => {},
    }
  }
  return ctx
}

// Feature-flag resolver — Sidebar consults this to decide which rendering
// path to take. Centralized here so tests can assert the flag contract.
export function isSidebarRegistryEnabled(): boolean {
  try {
    // Vite exposes env as import.meta.env; guard for non-Vite test envs.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const env = (import.meta as any)?.env || {}
    const flag = env.VITE_SIDEBAR_REGISTRY
    return flag === '1' || flag === 'true' || flag === true
  } catch {
    return false
  }
}
